import { Link, LinkProps, SxProps, Theme } from "@mui/material";
import { isNil } from "lodash-es";
import { forwardRef } from "react";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router-dom";

import AppTooltip, { AppTooltipProps } from "../Tooltip/AppTooltip";

export interface AppLinkProps extends LinkProps {
  /** If true - wraps children with the link, false - children are rendered directly. */
  isEnabled?: boolean;
  to: RouterLinkProps["to"] | undefined;
  sx?: SxProps<Theme>;
  /** When set, tooltip is rendered. */
  tooltipTitle?: string;
  tooltipProps?: Partial<AppTooltipProps>;
  /** Allows to disable tooltip */
  withTooltip?: boolean;
  /** Inherit some styles like color. */
  isInheritStyles?: boolean;

  /** Disable link specific styles like color, text decoration, etc. */
  disableLinkStyles?: boolean;
  disableTextDecoration?: boolean;
  icon?: React.ReactElement;
  children?: React.ReactNode;
}

export default forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(
  {
    isEnabled = true,
    to,
    sx,
    tooltipTitle,
    tooltipProps,
    withTooltip,
    isInheritStyles,
    disableLinkStyles,
    disableTextDecoration,
    icon,
    children,
    ...other
  }: AppLinkProps,
  ref,
) {
  const tooltipEnabled = isNil(withTooltip) ? !!tooltipTitle : withTooltip;

  if (!isEnabled) {
    return children;
  }

  return (
    <AppTooltip
      isEnabled={tooltipEnabled}
      {...tooltipProps}
      title={tooltipTitle || tooltipProps?.title}
    >
      <Link
        ref={ref}
        component={to ? RouterLink : "a"}
        to={to}
        fontSize='inherit'
        sx={{
          display: "inline",
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          textDecorationColor: "inherit",
          // "& *": { textDecoration: "underline", textDecorationColor: "inherit" },
          ...(isInheritStyles
            ? {
                color: "inherit",
              }
            : undefined),
          ...(disableLinkStyles
            ? {
                color: "inherit",
                textDecoration: "inherit",
                textDecorationColor: "inherit",
              }
            : undefined),
          ...(disableTextDecoration
            ? {
                textDecoration: "none",
              }
            : undefined),
          ...(sx || {}),
        }}
        {...other}
      >
        {icon && <span style={{ marginRight: "4px" }}>{icon} </span>}
        {children}
      </Link>
    </AppTooltip>
  );
});
